import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import SetupProgressCard from '../../features/onboarding/SetupProgressCard';
import { limitsNav } from '../../features/onboarding/onboardingChecklist';
import { useOnboardingChecklist } from '../../features/onboarding/useOnboardingChecklist';
import { relativeTime } from '../../shared/time';
import {
  Breakdown,
  BreakdownSkeleton,
  Button,
  FiguresSummary,
  HUE_TINT,
  HUE_TINT_HOVER,
  type Hue,
  IconArrowRight,
  IconCalendar,
  IconCheckCircle,
  IconDollar,
  IconInfo,
  IconMessage,
  IconPhone,
  IconReceipt,
  IconTask,
  IconUsers,
  IconVoicemail,
  QueryGate,
  Skeleton,
  StatusPill,
  SummaryFigure,
  SummaryFigureSkeleton,
  Surface,
} from '../../ui/v2';
import { HueGlyph, LiveDot, serif } from './dashboardChrome';
import {
  type ActivityItem,
  type AttentionGroup,
  type AttentionKind,
  callReasonLinkCount,
  type CallIntelligence,
  type FigureDef,
  type NeedsYou as NeedsYouData,
  type QueueItem,
  GROUP_META,
  activityWhen,
  arthurStatus,
  buildFigures,
  connectLabel,
  figuresReason,
  fmtCover,
  formatClock,
  formatToday,
  greeting,
  heroReport,
  monthlyIntentFrom,
  intelligenceRows,
  isFresh,
  latestCallAt,
  monthToDate,
  orderedGroups,
  queueItems,
  totalAttention,
} from './dashboardModel';
import {
  useCallIntelligence,
  useDashboardFigures,
  useNeedsYou,
  useStoreStatus,
  useTurnOnEvents,
  useUpdateReservationCover,
} from './dashboardQueries';

const useClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
};

/**
 * Home: an editorial greeting, the navy performance hero, the KPI strip,
 * a full-width action queue, then call reasons and recent calls side by side.
 *
 * Deferred, because no existing read can fill them without inventing a
 * number or a control: global search, setup progress, revenue attribution,
 * staff hours, resolution rate, peak-call forecast, teach-Arthur, and the
 * wins band. The development preview at `/design-preview/dashboard` still
 * shows those with labelled sample data.
 */
const NOT_LIVE = {
  label: 'Not live yet',
  tone: 'neutral' as const,
  hollow: true,
};

export default function Dashboard() {
  const { user } = useAuth();
  const now = useClock();
  const store = useStoreStatus();
  const checklist = useOnboardingChecklist();
  // Before go-live, calls to the store's number don't reach Arthur yet, so
  // "Arthur is answering" and an all-time report would both mislead.
  const preLive = limitsNav(checklist.data);
  // Both wait for the checklist, so a store that isn't live never sees them
  // flash up first. A checklist that can't load doesn't hold them back.
  const knowsLive = !(
    checklist.isPending && checklist.fetchStatus === 'fetching'
  );
  const status = !knowsLive
    ? null
    : preLive
      ? NOT_LIVE
      : arthurStatus(store.data?.busyStatus);
  const figures = useDashboardFigures();
  const intelligence = useCallIntelligence();
  const monthReasons = useCallIntelligence('month');
  const needs = useNeedsYou();
  const activity = figures.data?.activity ?? [];

  return (
    <>
      <PageMeta
        title="AskArthur | Dashboard"
        description="Action items today"
      />

      <div className="av2 bg-transparent pb-10">
        <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 px-1">
          <div className="min-w-0">
            <h1
              className={clsx(
                serif,
                'text-[34px] font-semibold leading-[38px] tracking-[-0.01em] text-v2-fg sm:text-v2-display-lg',
              )}
            >
              {greeting(now)}, {user?.first_name || 'there'}
            </h1>
            <p className="v2-num mt-1.5 text-v2-13 text-v2-fg-3">
              {formatToday(now)} · {formatClock(now)}
            </p>
          </div>
          {status && (
            <Link
              to={preLive ? '/getting-started' : '/store-info'}
              title={preLive ? 'Open Getting started' : 'Open Store info'}
              className={clsx(
                'inline-flex h-8 items-center gap-2 rounded-full border px-3 text-v2-12 font-semibold shadow-v2-card',
                status.tone === 'success' &&
                  'border-v2-line bg-v2-surface text-v2-fg-2',
                status.tone === 'warning' &&
                  'border-v2-warning/40 bg-v2-warning/[0.08] text-v2-warning',
                status.tone === 'neutral' &&
                  'border-v2-line bg-v2-surface text-v2-fg-3',
              )}
            >
              {status.tone === 'success' ? (
                <LiveDot />
              ) : (
                <span
                  aria-hidden
                  className={clsx(
                    'h-2 w-2 rounded-full',
                    status.hollow
                      ? 'border border-v2-fg-4 bg-transparent'
                      : 'bg-v2-warning',
                  )}
                />
              )}
              {status.label}
            </Link>
          )}
        </header>

        <div className="mt-8 grid gap-5 lg:gap-6">
          <SetupProgressCard />
          {knowsLive && !preLive && (
            <PerformanceHero
              query={figures}
              store={store}
              monthlyIntent={monthlyIntentFrom(monthReasons.data)}
              monthReady={monthReasons.status !== 'pending'}
            />
          )}
          <KpiStrip
            query={figures}
            period={monthToDate(store.data?.todayLocal ?? null, now)}
          />
          <NeedsYouCard query={needs} />

          <div className="grid items-start gap-5 lg:grid-cols-2 lg:gap-6">
            <CallIntelligenceCard
              query={intelligence}
              className={activity.length === 0 ? 'lg:col-span-2' : undefined}
            />
            {activity.length > 0 && <LiveActivity items={activity} />}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Performance hero + secondary KPIs
// ---------------------------------------------------------------------------

const heroLineClass = clsx(
  serif,
  'text-balance text-[30px] font-medium leading-[34px] text-v2-fg sm:text-[38px] sm:leading-[42px]',
);

const PerformanceHero: React.FC<{
  query: ReturnType<typeof useDashboardFigures>;
  store: ReturnType<typeof useStoreStatus>;
  monthlyIntent: ReturnType<typeof monthlyIntentFrom>;
  monthReady: boolean;
}> = ({ query, store, monthlyIntent, monthReady }) => {
  const data = query.data;
  // A still-loading pulse is not "unknown". Choosing a sentence before the
  // store id, local date, paused state, and this month's topics arrive
  // swaps the line after paint.
  const storeReady = store.status !== 'pending';
  const pulse = storeReady ? store.data : undefined;
  const headline =
    data && storeReady && monthReady
      ? heroReport({
          stats: data.stats,
          gating: data.gating,
          busyStatus: pulse?.busyStatus ?? null,
          routingActive: pulse?.routingActive ?? null,
          todayLocal: pulse?.todayLocal ?? null,
          storeId: pulse?.storeId ?? null,
          monthlyIntent,
        })
      : null;
  const lastCall = data ? latestCallAt(data.activity) : null;
  const connect =
    data && headline && !headline.suppressConnect
      ? connectLabel(data.gating)
      : null;
  const reason = data ? figuresReason(data.gating) : null;

  return (
    <section
      aria-labelledby="dashboard-report"
      className="v2-hero relative overflow-clip rounded-[22px] shadow-v2-hero"
    >
      <div className="v2-inverse flex flex-col gap-6 px-6 py-7 sm:px-8 lg:flex-row lg:items-center lg:gap-10 lg:px-9 lg:py-8">
        <div className="min-w-0 flex-1">
          <h2
            id="dashboard-report"
            className="mb-3 text-v2-11 font-semibold uppercase tracking-[0.16em] text-v2-accent"
          >
            Arthur’s report
          </h2>
          {headline ? (
            <p className={heroLineClass}>
              {headline.segments.map((segment, index) =>
                segment.emphasis ? (
                  <span
                    key={index}
                    className="font-bold underline decoration-v2-accent/60 decoration-2 underline-offset-[6px]"
                  >
                    {segment.text}
                  </span>
                ) : (
                  <span key={index}>{segment.text}</span>
                ),
              )}
            </p>
          ) : (
            (query.status === 'pending' ||
              store.status === 'pending' ||
              !monthReady) && (
              <Skeleton width={420} className="h-9 max-w-full" />
            )
          )}
          {headline?.secondary && (
            <p
              className="mt-3 text-v2-14 text-v2-fg-2"
              title={headline.secondaryTitle || undefined}
            >
              {headline.secondary}
            </p>
          )}
        </div>
        {(lastCall || connect || headline?.suppressConnect) && (
          <div className="flex flex-col items-start gap-3 lg:items-end">
            {lastCall && (
              <span className="inline-flex items-center gap-2 text-v2-12 text-v2-fg-2">
                {isFresh(lastCall) && <LiveDot />}
                Last call {relativeTime(lastCall)}
              </span>
            )}
            {headline?.suppressConnect && (
              <span className="text-v2-12 font-medium text-v2-fg-2">
                Paused
              </span>
            )}
            {connect && (
              <Link
                to="/integrations"
                title={reason ?? undefined}
                className="inline-flex items-center gap-2 rounded-v2-lg border border-white/15 bg-white/[0.07] px-4 py-2.5 text-v2-13 font-medium text-v2-fg transition-colors hover:bg-white/[0.12]"
              >
                {connect}
                <IconArrowRight size={13} />
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const FIGURE_ICON: Record<FigureDef['icon'], React.ReactNode> = {
  phone: <IconPhone size={13} />,
  calendar: <IconCalendar size={13} />,
  users: <IconUsers size={13} />,
  receipt: <IconReceipt size={13} />,
  dollar: <IconDollar size={13} />,
  voicemail: <IconVoicemail size={13} />,
  message: <IconMessage size={13} />,
};

const figureActionClass = (hue: Hue) =>
  clsx(
    'group inline-flex h-7 items-center gap-1.5 rounded-v2 px-2.5 text-v2-12 font-semibold transition-colors disabled:pointer-events-none',
    HUE_TINT[hue],
    HUE_TINT_HOVER[hue],
  );

const TurnOnEvents: React.FC<{ label: string; reason: string; hue: Hue }> = ({
  label,
  reason,
  hue,
}) => {
  const turnOn = useTurnOnEvents();
  return (
    <button
      type="button"
      title={turnOn.isError ? 'Could not turn on. Try again.' : reason}
      disabled={turnOn.isPending}
      onClick={() => turnOn.mutate()}
      className={clsx(figureActionClass(hue), 'disabled:opacity-60')}
    >
      {turnOn.isPending ? 'Turning on…' : label}
    </button>
  );
};

const KpiStripHeader: React.FC<{ period: string; cover?: number | null }> = ({
  period,
  cover,
}) => (
  <>
    <h2 className="text-v2-14 font-bold tracking-[-0.01em] text-v2-fg">
      This month
    </h2>
    <span className="v2-num text-v2-13 text-v2-fg-3">{period}</span>
    {cover != null && (
      <span className="ml-auto text-v2-12 text-v2-fg-3">
        <ReservationCoverNote cover={cover} />
      </span>
    )}
  </>
);

const KpiStrip: React.FC<{
  query: ReturnType<typeof useDashboardFigures>;
  period: string;
}> = ({ query, period }) => (
  <QueryGate
    query={query}
    errorTitle="This month’s figures could not be loaded."
    skeleton={
      <FiguresSummary
        columns={6}
        header={<KpiStripHeader period={period} />}
        aria-label="This month"
        aria-busy
        className="shadow-v2-card"
      >
        {Array.from({ length: 6 }, (_, i) => (
          <SummaryFigureSkeleton key={i} icon />
        ))}
      </FiguresSummary>
    }
  >
    {({ stats, gating, channels }) => {
      const rows = buildFigures(stats, gating, channels);
      const cover = rows.some((f) => f.estimate)
        ? stats.reservationCover
        : null;
      return (
        <FiguresSummary
          aria-label="This month"
          header={<KpiStripHeader period={period} cover={cover} />}
        >
          {rows.map((f) => (
            <SummaryFigure
              key={f.key}
              label={f.label}
              value={
                f.estimate && cover != null ? (
                  <>
                    {f.month}
                    <span
                      aria-hidden
                      className="relative -top-1 ml-px text-v2-20 font-normal text-v2-fg-4"
                    >
                      *
                    </span>
                  </>
                ) : (
                  f.month
                )
              }
              icon={FIGURE_ICON[f.icon]}
              hue={f.hue}
              action={
                f.connect ? (
                  <Link
                    to="/integrations"
                    title={f.connect.reason}
                    className={figureActionClass(f.hue)}
                  >
                    {f.connect.label}
                    <IconArrowRight
                      size={12}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                ) : (
                  f.turnOn && <TurnOnEvents {...f.turnOn} hue={f.hue} />
                )
              }
              secondary={f.secondary}
            />
          ))}
        </FiguresSummary>
      );
    }}
  </QueryGate>
);

const ReservationCoverNote: React.FC<{ cover: number }> = ({ cover }) => {
  const update = useUpdateReservationCover();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const start = () => {
    setDraft(Number.isInteger(cover) ? String(cover) : cover.toFixed(2));
    setError('');
    setEditing(true);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const amount = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(amount) || amount < 0) {
      setError('Enter a dollar amount.');
      return;
    }
    try {
      await update.mutateAsync(amount);
      setEditing(false);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="flex flex-wrap items-center gap-1.5">
        <label className="sr-only" htmlFor="reservation-cover">
          Average revenue per guest
        </label>
        <span aria-hidden>$</span>
        <input
          id="reservation-cover"
          inputMode="decimal"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="w-16 rounded-v2 border border-v2-line bg-v2-surface px-1.5 py-0.5 text-v2-12 text-v2-fg"
        />
        <button
          type="submit"
          disabled={update.isPending}
          className="font-medium text-v2-accent hover:underline disabled:opacity-60"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="font-medium text-v2-fg-3 hover:underline"
        >
          Cancel
        </button>
        {error && <span className="basis-full text-v2-danger">{error}</span>}
      </form>
    );
  }

  return (
    <span>
      *Reservation revenue based on {fmtCover(cover)} per guest.{' '}
      <button
        type="button"
        onClick={start}
        className="font-medium text-v2-accent hover:underline"
      >
        Change
      </button>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Recent activity
// ---------------------------------------------------------------------------

const ACTIVITY_ICON: Record<ActivityItem['icon'], React.ReactNode> = {
  phone: <IconPhone size={15} />,
  calendar: <IconCalendar size={15} />,
  receipt: <IconReceipt size={15} />,
};

const ActivityRow: React.FC<{ item: ActivityItem }> = ({ item }) => {
  const fresh = isFresh(item.at);
  const when = activityWhen(item.at);
  return (
    <li>
      <Link
        to={item.href}
        className={clsx(
          'group flex w-full items-start gap-3 rounded-v2-lg px-2.5 py-3 text-left transition-colors hover:bg-v2-sunken/70',
          fresh && 'bg-v2-accent/[0.06] hover:bg-v2-accent/[0.09]',
        )}
      >
        <span
          className={clsx(
            'v2-num hidden w-[72px] shrink-0 pt-1.5 text-right text-v2-11 font-semibold sm:block',
            fresh
              ? 'uppercase tracking-[0.08em] text-v2-accent'
              : 'text-v2-fg-4',
          )}
        >
          {when}
        </span>
        <HueGlyph hue={item.hue}>{ACTIVITY_ICON[item.icon]}</HueGlyph>
        <span className="min-w-0 flex-1">
          <span className="block text-v2-14 font-semibold text-v2-fg">
            {item.who && (
              <>
                {item.who}
                <span className="font-normal text-v2-fg-3"> — </span>
              </>
            )}
            {item.what}
          </span>
          {(when || item.meta) && (
            <span className="block text-v2-12 text-v2-fg-3">
              {when && (
                <span
                  className={clsx(
                    'v2-num sm:hidden',
                    fresh && 'font-semibold text-v2-accent',
                  )}
                >
                  {when}
                  {item.meta ? ' · ' : ''}
                </span>
              )}
              {item.meta}
            </span>
          )}
        </span>
        {item.value && (
          <span className="shrink-0 pt-0.5 text-right">
            <span className="v2-num block text-v2-14 font-bold text-v2-fg">
              {item.value.amount}
            </span>
            <span className="block text-v2-11 text-v2-fg-3">
              {item.value.label}
            </span>
          </span>
        )}
      </Link>
    </li>
  );
};

const LiveActivity: React.FC<{ items: ActivityItem[] }> = ({ items }) => (
  <Surface
    as="section"
    aria-labelledby="dashboard-activity"
    className="px-3 pb-2 pt-5"
  >
    <div className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 px-2.5">
      <h2
        id="dashboard-activity"
        className="text-v2-18 font-bold tracking-[-0.015em] text-v2-fg"
      >
        Recent calls
      </h2>
      <span className="text-v2-13 text-v2-fg-3">
        Calls, reservations and orders
      </span>
    </div>
    <ol className="divide-y divide-v2-line/70">
      {items.map((item) => (
        <ActivityRow key={item.id} item={item} />
      ))}
    </ol>
  </Surface>
);

// ---------------------------------------------------------------------------
// Call reasons
// ---------------------------------------------------------------------------

const CallIntelligenceCard: React.FC<{
  query: ReturnType<typeof useCallIntelligence>;
  className?: string;
}> = ({ query, className }) => (
  <Surface
    as="section"
    aria-labelledby="dashboard-why"
    className={clsx('flex flex-col p-5 lg:p-6', className)}
  >
    <WhyGuestsCall query={query} />
  </Surface>
);

const WhyGuestsCall: React.FC<{
  query: ReturnType<typeof useCallIntelligence>;
}> = ({ query }) => {
  const tagged = query.data?.tagged_calls ?? 0;
  const callCount = query.data ? callReasonLinkCount(query.data) : 0;
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2
          id="dashboard-why"
          className="text-v2-18 font-bold tracking-[-0.015em] text-v2-fg"
        >
          Call reasons
        </h2>
        {tagged > 0 && (
          <span className="v2-num text-v2-13 text-v2-fg-3">
            {tagged.toLocaleString()} {tagged === 1 ? 'call' : 'calls'} tagged
          </span>
        )}
        {query.data && (
          <span className="ml-auto inline-flex h-6 items-center rounded-full bg-v2-sunken px-2.5 text-v2-11 font-semibold text-v2-fg-2">
            {query.data.scope === 'all_time' ? 'All time' : 'This period'}
          </span>
        )}
      </div>
      <div className="mt-5 flex flex-1 flex-col">
        <QueryGate
          query={query}
          errorTitle="Call reasons could not be loaded."
          skeleton={<BreakdownSkeleton />}
        >
          {(intel: CallIntelligence | null) => {
            const rows = intel ? intelligenceRows(intel) : [];
            if (rows.length === 0) {
              return (
                <CalmState
                  icon={<IconPhone size={18} />}
                  title="No calls tagged yet"
                  description="Arthur sorts every call by why the guest rang."
                />
              );
            }
            return (
              <div>
                <Breakdown
                  rows={rows}
                  noun="calls"
                  palette="multi"
                  size="lg"
                  aria-label="Call reasons"
                />
                {callCount > 0 && (
                  <Link
                    to="/calls"
                    className="mt-2 inline-flex h-7 items-center gap-1 rounded-v2 px-1.5 text-v2-12 font-medium text-v2-fg-3 hover:bg-v2-sunken hover:text-v2-accent"
                  >
                    <span className="v2-num">
                      {callCount.toLocaleString()}{' '}
                      {callCount === 1 ? 'call' : 'calls'}
                    </span>
                    <IconArrowRight size={12} />
                  </Link>
                )}
              </div>
            );
          }}
        </QueryGate>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Action items — a full-width queue. Rows read across, like recent calls,
// so the width carries who, the reason, and the status instead of stacking
// a narrow card.
// ---------------------------------------------------------------------------

const KIND_ICON: Record<AttentionKind, React.ReactNode> = {
  sms: <IconMessage size={15} />,
  voicemail: <IconVoicemail size={15} />,
  payment: <IconDollar size={15} />,
  approval: <IconTask size={15} />,
  event_inquiry: <IconCalendar size={15} />,
  catering_inquiry: <IconCalendar size={15} />,
  knowledge_question: <IconInfo size={15} />,
};

const QueueSkeleton: React.FC = () => (
  <div aria-busy className="mt-2 divide-y divide-v2-line/70">
    {Array.from({ length: 3 }, (_, i) => (
      <div
        key={i}
        aria-hidden
        className="flex items-center gap-3 px-2.5 py-3.5"
      >
        <Skeleton className="h-8 w-8 rounded-[10px]" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton width={`${32 - i * 6}%`} className="h-3.5" />
          <Skeleton width={`${58 - i * 8}%`} className="h-3" />
        </div>
        <Skeleton width={72} className="h-6 rounded-full" />
      </div>
    ))}
  </div>
);

const NeedsYouCard: React.FC<{
  query: ReturnType<typeof useNeedsYou>;
}> = ({ query }) => (
  <Surface
    as="section"
    aria-labelledby="dashboard-needs"
    className="px-3 pb-3 pt-5"
  >
    <div className="mb-1 px-2.5">
      <h2
        id="dashboard-needs"
        className="flex items-center gap-2 text-v2-18 font-bold tracking-[-0.015em] text-v2-fg"
      >
        Action items
        <AttentionCount
          value={query.data ? totalAttention(query.data.groups) : undefined}
        />
      </h2>
    </div>
    <QueryGate
      query={query}
      errorTitle="Action items could not be loaded."
      skeleton={<QueueSkeleton />}
    >
      {(data) => <NeedsYou data={data} onRetry={() => query.refetch()} />}
    </QueryGate>
  </Surface>
);

const AttentionCount: React.FC<{ value?: number }> = ({ value }) =>
  typeof value === 'number' && value > 0 ? (
    <span className="v2-num inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-v2-warning/[0.14] px-1.5 text-v2-12 font-semibold text-v2-warning">
      {value}
    </span>
  ) : null;

const CalmState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  tone?: 'success' | 'neutral';
}> = ({ icon, title, description, tone = 'neutral' }) => (
  <div
    className={clsx(
      'flex min-h-[120px] flex-1 flex-col items-center justify-center gap-3 rounded-v2-lg px-5 py-6 text-center',
      tone === 'success' ? 'bg-v2-success/[0.06]' : 'bg-v2-sunken/70',
    )}
  >
    <span
      aria-hidden
      className={clsx(
        'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
        tone === 'success'
          ? 'bg-v2-success/[0.14] text-v2-success'
          : 'bg-v2-surface text-v2-fg-3 ring-1 ring-inset ring-v2-line',
      )}
    >
      {icon}
    </span>
    <span className="flex min-w-0 flex-col items-center gap-0.5">
      <span className="text-v2-15 font-semibold text-v2-fg">{title}</span>
      <span className="text-v2-13 text-v2-fg-3">{description}</span>
    </span>
  </div>
);

const NeedsYou: React.FC<{ data: NeedsYouData; onRetry: () => void }> = ({
  data,
  onRetry,
}) => {
  const groups = orderedGroups(data.groups);
  const items = queueItems(data.groups);
  const failed = data.failed.map((kind) => GROUP_META[kind].label);

  const failure = failed.length > 0 && (
    <p className="mt-3 flex flex-wrap items-center gap-x-2 text-v2-12 text-v2-fg-3">
      {failed.join(', ')} could not be loaded.
      <Button size="sm" variant="ghost" onClick={onRetry}>
        Retry
      </Button>
    </p>
  );

  if (groups.length === 0) {
    return (
      <>
        <div className="mx-2.5 mt-3 flex items-center gap-3 rounded-v2-lg bg-v2-success/[0.06] px-4 py-4">
          <span
            aria-hidden
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-v2-success/[0.14] text-v2-success"
          >
            <IconCheckCircle size={20} />
          </span>
          <span className="min-w-0">
            <span className="block text-v2-15 font-semibold text-v2-fg">
              No action items right now
            </span>
            <span className="block text-v2-13 text-v2-fg-3">
              When a guest needs a person, it lands here first.
            </span>
          </span>
        </div>
        {failure}
      </>
    );
  }

  return (
    <>
      <ol aria-label="Action items" className="mt-2 divide-y divide-v2-line/70">
        {items.map((item) => (
          <QueueRow key={item.id} item={item} />
        ))}
      </ol>
      <div className="mx-1 mt-1 flex flex-wrap items-center gap-x-1 gap-y-1 border-t border-v2-line px-1.5 pt-2">
        {groups.map((group) => (
          <GroupLink key={group.kind} group={group} />
        ))}
      </div>
      {failure}
    </>
  );
};

const GroupLink: React.FC<{ group: AttentionGroup }> = ({ group }) => {
  const meta = GROUP_META[group.kind];
  return (
    <Link
      to={meta.href}
      aria-label={`Open ${meta.label.toLowerCase()} · ${group.count}`}
      className="inline-flex h-7 items-center gap-1.5 rounded-v2 px-2 text-v2-12 font-medium text-v2-accent hover:bg-v2-accent/[0.08]"
    >
      {meta.label}
      <span className="v2-num font-normal text-v2-fg-3">{group.count}</span>
    </Link>
  );
};

const QueueRow: React.FC<{ item: QueueItem }> = ({ item }) => {
  const meta = GROUP_META[item.kind];
  const urgent = item.status.tone === 'danger';
  const when = relativeTime(item.at);
  return (
    <li
      className={clsx(
        urgent && 'bg-v2-warning/[0.05] dark:bg-v2-warning/[0.08]',
      )}
    >
      <Link
        to={item.href}
        className={clsx(
          'group flex w-full items-start gap-3 rounded-v2-lg px-2.5 py-3.5 text-left transition-colors hover:bg-v2-sunken/70 focus-visible:bg-v2-accent-soft',
          urgent && 'hover:bg-v2-warning/[0.09]',
        )}
      >
        <HueGlyph hue={meta.hue} className="mt-0.5">
          {KIND_ICON[item.kind]}
        </HueGlyph>
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-3">
            <span className="flex min-w-0 items-baseline gap-2">
              <span
                className={clsx(
                  'min-w-0 truncate text-v2-14 font-semibold',
                  item.who ? 'text-v2-fg' : 'text-v2-fg-2',
                )}
              >
                {item.who ?? item.fallback}
              </span>
              <span className="shrink-0 truncate text-v2-13 text-v2-fg-3">
                {meta.one}
                {item.detail && ` · ${item.detail}`}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-3 pt-0.5">
              <StatusPill tone={item.status.tone} hollow={item.status.hollow}>
                {item.status.label}
              </StatusPill>
              {when && (
                <span className="v2-num hidden w-16 text-right text-v2-12 text-v2-fg-3 sm:block">
                  {when}
                </span>
              )}
            </span>
          </span>
          {item.summary && (
            <span className="mt-1 line-clamp-2 block text-v2-13 leading-5 text-v2-fg-3">
              {item.summary}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
};
